import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SendHorizontalIcon } from 'lucide-react';
import { askCarli, AgentMessage } from '../utils/carliAgent';
const carliIconUrl = 'https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg';
const easeOut = [0.23, 1, 0.32, 1] as const;

// Prompts adapt to wherever the user currently is in the portal.
const promptsByView: Record<string, string[]> = {
  carli: ['What needs my attention today?', 'Where is recovery trending?', 'What should we promote next?'],
  inventory: ['Which upload is underperforming?', 'What is still sitting unsold?', 'How was this forecast built?'],
  performance: ['Why did recovery change this month?', 'Which channel recovers best?', 'What is selling fastest?'],
  marketing: ['Which channel has the best ROAS?', 'Where should the next $10K go?', 'How is the Vibe spend performing?'],
  controls: ['What index price do you recommend?', 'Should live selling stay held?', 'Which channels should be on?'],
  messages: ['Summarize what brands are asking', 'Draft a reply to this brand', 'What needs a response today?'],
  finances: ['When is the next payout?', 'What is unreconciled?', 'How much has been paid out?'],
  demand: ['Where is demand missing?', 'Which campaigns need attention?', 'What creative should we run next?'],
  pipeline: ['Which brands signed but never activated?', 'What contracts are waiting?', 'Who should I follow up with?']
};
const viewLabels: Record<string, string> = {
  carli: 'Home',
  inventory: 'Inventory',
  performance: 'Recovery',
  marketing: 'Marketing',
  controls: 'Settings',
  messages: 'Messages',
  finances: 'Payouts',
  demand: 'Demand',
  pipeline: 'Pipeline'
};
export function CarliDock({
  activeView,
  selectedOwner,
  contextSummary
}: {
  activeView: string;
  selectedOwner: string;
  contextSummary?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const viewLabel = viewLabels[activeView] ?? 'Portal';
  const brandLabel = selectedOwner || 'All brands';
  const prompts = promptsByView[activeView] ?? promptsByView.carli;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, open]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    const next: AgentMessage[] = [...messages, {
      role: 'user',
      content: trimmed
    }];
    setMessages(next);
    setDraft('');
    setThinking(true);
    setError(null);
    try {
      const reply = await askCarli({
        messages: next,
        context: [contextSummary ?? `The user is on the "${viewLabel}" screen. Brand scope: ${brandLabel}.`, 'HOW TO ANSWER: You have visibility into the entire portal — every brand, inventory submission, order file, marketing channel, demand campaign, message thread, and sales pipeline record listed above. Answer questions about any of it, regardless of which screen the user is on. Never tell the user to switch the brand selector or navigate elsewhere to get an answer you can already give from this context. Only say you lack a data point if it genuinely is not above. Keep it to a few sentences unless they ask for depth.'].filter(Boolean).join('\n\n'),
        maxTokens: 600
      });
      setMessages(current => [...current, {
        role: 'assistant',
        content: reply
      }]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Carli is unavailable right now.');
    } finally {
      setThinking(false);
    }
  };
  return <>
      <AnimatePresence data-id="element-279" data-mp="1o96we1wmfpk1">
        {!open && <motion.button key="bubble" type="button" onClick={() => setOpen(true)} initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.16,
        ease: easeOut
      }} className="fixed bottom-5 right-5 z-40 flex items-center gap-2.5 rounded-full bg-[#BB5E40] py-2.5 pl-2.5 pr-5 text-sm font-semibold text-cream shadow-lg transition-colors hover:bg-[#A34E33] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/40 focus:ring-offset-2" data-id="element-280" data-mp="1jbf4v8zw25gd">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream" data-id="element-281" data-mp="rlses1f8dmkf">
              <CarliMark className="h-5 w-5" data-id="element-282" data-mp="ml4rnqu4hpvh" />
            </span>
            Ask Carli
          </motion.button>}
      </AnimatePresence>

      <AnimatePresence data-id="element-283" data-mp="338dsls6ysusp">
        {open && <motion.section key="dock" aria-label="Ask Carli" initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.24,
        ease: easeOut
      }} className="fixed inset-x-0 bottom-0 z-40 border-t border-[#BB5E40]/30 bg-paper shadow-[0_-8px_32px_rgba(26,26,26,0.12)]" data-id="element-284" data-mp="339fcmm5ykrmk">
            <div className="mx-auto flex h-[min(58vh,460px)] max-w-5xl flex-col px-5 py-4 sm:px-8" data-id="element-285" data-mp="3lxan9x69xpbg">
              <div className="flex items-center justify-between gap-4 border-b border-ink/10 pb-3" data-id="element-286" data-mp="3qygko8avq0rb">
                <div className="flex min-w-0 items-center gap-3" data-id="element-287" data-mp="3uyqyu88trxh9">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#BB5E40]/25 bg-cream" data-id="element-288" data-mp="1wkp7iytccyr4">
                    <CarliMark className="h-5 w-5" data-id="element-289" data-mp="20lsq3t2rx8w1" />
                  </span>
                  <div className="min-w-0" data-id="element-290" data-mp="33x7xmov91ozp">
                    <p className="text-sm font-semibold" data-id="element-291" data-mp="2yxbraiz2xlgz">Ask Carli</p>
                    <p className="truncate text-xs text-ink/55" data-id="element-292" data-mp="2yz6e62twvnnp">
                      Viewing {viewLabel} · {brandLabel}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setOpen(false)} aria-label="Collapse Ask Carli" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-293" data-mp="38tybr6nhuaih">
                  <ChevronDownIcon className="h-4 w-4" data-id="element-294" data-mp="eodyaqmzrgi" />
                </button>
              </div>

              <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto py-4" style={{
            scrollbarGutter: 'stable'
          }} data-id="element-295" data-mp="3rn28j0w0d3hb">
                {messages.length === 0 && !thinking && <div data-id="element-296" data-mp="4icl4sbcwl18">
                    <p className="text-sm leading-relaxed text-ink/65" data-id="element-297" data-mp="4iw50ekkhjhw">
                      I can see the {viewLabel} screen for {brandLabel}. Ask me about what is on it, or go deeper into the numbers behind it.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-298" data-mp="f2exh6jvu1gr">
                      {prompts.map(prompt => <button key={prompt} type="button" onClick={() => send(prompt)} className="rounded-full border border-[#BB5E40]/30 bg-[#BB5E40]/[0.06] px-3.5 py-2 text-xs font-semibold text-[#94452E] transition-colors hover:border-[#BB5E40]" data-id="element-299" data-mp="j3gzfv56ormf">
                          {prompt}
                        </button>)}
                    </div>
                  </div>}

                {messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-300" data-mp="3nhymtuhfmzqj" data-mpi={index}>
                    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'border border-ink/10 bg-cream/50 text-ink/80'}`} data-id="element-301" data-mp="6v3rhfodon0b">
                      {message.content}
                    </p>
                  </div>)}

                {thinking && <p className="text-sm text-ink/50" data-id="element-302" data-mp="3k2yf7210sz0j">Carli is thinking…</p>}
                {error && <p className="text-sm text-[#94452E]" data-id="element-303" data-mp="3f9m2nq9m8io5">{error}</p>}
              </div>

              <form onSubmit={event => {
            event.preventDefault();
            send(draft);
          }} className="flex items-center gap-2 border-t border-ink/10 pt-3" data-id="element-304" data-mp="3aasso7tjf9sq">
                <input value={draft} onChange={event => setDraft(event.target.value)} placeholder={`Ask Carli about ${viewLabel.toLowerCase()}…`} aria-label="Ask Carli a question" className="min-w-0 flex-1 rounded-full border border-ink/15 bg-paper px-4 py-3 text-sm focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-305" data-mp="31cyussg4k83g" />
                <button type="submit" disabled={thinking || draft.trim().length === 0} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#BB5E40] text-cream transition-colors hover:bg-[#A34E33] disabled:opacity-45" aria-label="Send" data-id="element-306" data-mp="1psgtobrqk3su">
                  <SendHorizontalIcon className="h-4 w-4" data-id="element-307" data-mp="1224ceaaloupl" />
                </button>
              </form>
            </div>
          </motion.section>}
      </AnimatePresence>
    </>;
}
function CarliMark({
  className
}: {
  className?: string;
}) {
  return <span aria-hidden="true" className={`inline-block shrink-0 bg-[#BB5E40] ${className ?? ''}`} style={{
    WebkitMaskImage: `url(${carliIconUrl})`,
    maskImage: `url(${carliIconUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-308" data-mp="ecs6pkehf4s1" />;
}